/* „Leg die App auf deinen Startbildschirm" — der Balken über der Anmeldung.
 *
 * Warum hier und nicht auf der Helferseite: der Installationsdialog gehört der
 * **Herkunft der App**. Eine Seite auf vimaanagarten.de kann
 * team.vimaanagarten.de grundsätzlich nicht installieren, egal wie der Knopf
 * heißt. Also steht das Angebot dort, wo es wirken kann — und genau dann, wenn
 * der Helfer ohnehin gerade ankommt.
 *
 * Zwei Ausgänge, kein dritter (siehe `lib/installieren.ts`):
 *   Android → ein Tipp, echter Systemdialog.
 *   iPhone  → dieselben drei Schritte, die auch auf der Helferseite stehen.
 */
import { useEffect, useState } from "react";

import { VGTIcon } from "../lib/vgt";
import { beiLageaenderung, installieren, lage, type InstallLage } from "../lib/installieren";

const SCHLUESSEL = "vgt_install_spaeter";

export function InstallBanner() {
  const [l, setL] = useState<InstallLage>(() => lage());
  const [offen, setOffen] = useState(false);
  const [versteckt, setVersteckt] = useState(() => {
    try { return sessionStorage.getItem(SCHLUESSEL) === "1"; } catch { return false; }
  });

  useEffect(() => beiLageaenderung(() => setL(lage())), []);

  // Läuft die App schon vom Startbildschirm, gibt es nichts anzubieten.
  if (l === "laeuft_schon" || versteckt) return null;

  const spaeter = () => {
    // Nur für diese Sitzung. Beim nächsten Öffnen im Browser fragen wir wieder —
    // ein dauerhaftes Wegklicken wäre genau die Falle, die der Balken verhindert.
    try { sessionStorage.setItem(SCHLUESSEL, "1"); } catch { /* Privatmodus */ }
    setVersteckt(true);
  };

  const antippen = () => {
    if (l !== "knopf") { setOffen(true); return; }
    // Kein `await` zwischen Tipp und Aufruf — der Browser verlangt die Geste.
    void installieren().then(erfolg => { if (!erfolg) setOffen(true); });
  };

  return (
    <>
      <div className="vgt-install" role="status">
        <span className="ic"><VGTIcon name="arrowup" /></span>
        <div className="tx">
          <b>Leg die App auf deinen Startbildschirm</b>
          <span>Dann startet sie wie eine normale App — und du bekommst Mitteilungen.</span>
        </div>
        <button className="vgt-install-btn" onClick={antippen}>
          {l === "knopf" ? "Installieren" : "So geht's"}
        </button>
        <button className="vgt-install-spaeter" onClick={spaeter} aria-label="Später">Später</button>
      </div>

      {offen && (
        <div className="vgt-sheet-back" onClick={() => setOffen(false)}>
          <div className="vgt-sheet" onClick={e => e.stopPropagation()}>
            <div className="vgt-sheet-grab" />
            <div className="vgt-sheet-title">Auf den Startbildschirm legen</div>
            {l === "anleitung_ios" ? (
              <>
                <div className="vgt-sheet-sub">
                  Auf dem iPhone geht das nur von Hand — und nur in <b>Safari</b>, nicht in Chrome.
                  Drei Schritte:
                </div>
                <ol className="vgt-schritte">
                  <li>Tippe unten auf das <b>Teilen-Symbol</b> — das Quadrat mit dem Pfeil nach oben.</li>
                  <li>Scrolle zu <b>„Zum Home-Bildschirm"</b> und tippe darauf.</li>
                  <li>Oben rechts auf <b>„Hinzufügen"</b>. Fertig — das Symbol heißt <b>Vimaana Team</b>.</li>
                </ol>
                <div className="vgt-hinweis"><span className="pin"><VGTIcon name="info" /></span><div>
                  Ohne diesen Schritt bekommst du auf dem iPhone <b>keine Mitteilungen</b>.
                  Daran können wir nichts ändern — das ist eine Vorgabe von Apple.
                </div></div>
              </>
            ) : (
              <>
                <div className="vgt-sheet-sub">
                  Dein Browser hat das Angebot gerade nicht parat. Von Hand geht es so:
                </div>
                <ol className="vgt-schritte">
                  <li>Tippe oben rechts auf die <b>drei Punkte</b>.</li>
                  <li>Wähle <b>„App installieren"</b> — oder <b>„Zum Startbildschirm hinzufügen"</b>.</li>
                  <li>Bestätigen. Fertig — das Symbol heißt <b>Vimaana Team</b>.</li>
                </ol>
              </>
            )}
            <div className="vgt-actions">
              <button className="vgt-cta" onClick={() => setOffen(false)}>Alles klar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
